import { Box3, Group, Vector3 } from 'three';
import { Easing, Tween } from '@tweenjs/tween.js';
import { EngineContext } from './engine-context';
import { ArrowHelper } from '../addons/load-helpers/arrowhelper';
import { OVERLAY_COLORS, arrowHead, disposeOverlay, overlayModelSize } from '../addons/visualization/overlay-style';
import { isMoldRemovalView, storedDrawAxis } from '../../utils/draw-direction-reference.util';

type DrawDirection = 'xp' | 'yp' | 'zp' | 'xm' | 'ym' | 'zm';

/**
 * Manages the draw-direction arrow helper and normal arrow helper.
 * Extracted from Engine to reduce class size.
 */
export class HelperManager {
  constructor(private ctx: EngineContext) {}

  drawDirectionVectorChange(direction?: DrawDirection): void {
    setTimeout(() => {
      const ddv = this.ctx.session.project.drawDirectionVector;

      if (direction) {
        ddv.xp = false;
        ddv.yp = false;
        ddv.zp = false;
        ddv.xm = false;
        ddv.ym = false;
        ddv.zm = false;
        ddv[direction] = true;

        if (ddv.xp && ddv.yp && ddv.zp && ddv.xm && ddv.ym && ddv.zm) {
          ddv['xp'] = true;
        }
      }

      if (!direction) {
        if (ddv.xp) direction = 'xp';
        else if (ddv.yp) direction = 'yp';
        else if (ddv.zp) direction = 'zp';
        else if (ddv.xm) direction = 'xm';
        else if (ddv.ym) direction = 'ym';
        else if (ddv.zm) direction = 'zm';
        else direction = 'xp';
      }

      const directions: Record<DrawDirection, Vector3> = {
        xp: new Vector3(1, 0, 0),
        yp: new Vector3(0, 1, 0),
        zp: new Vector3(0, 0, 1),
        xm: new Vector3(-1, 0, 0),
        ym: new Vector3(0, -1, 0),
        zm: new Vector3(0, 0, -1)
      };
      // The stored direction is the part removal one; a die-casting project
      // viewed in mold removal terms shows the opposite axis, and so does the arrow.
      const shown = storedDrawAxis(direction, isMoldRemovalView(this.ctx.session.project));
      // Drawn along UCS-frame axes, in the UCS-frame holder.
      const { center, box } = this.ctx.ucsFrameObjectInfo;
      this.createAndAnimateArrow(center, directions[shown], box, 10);
      if (!this.ctx.helpersVisible) this.hideArrow();
    });
  }

  createAndAnimateArrow(startPosition: Vector3, direction: Vector3, boundingBox: Box3, distance: number): void {
    if (this.ctx.arrowHelper) this.disposeArrow();
    if (this.ctx.arrowHelperTween) this.ctx.arrowHelperTween.stop();

    const normalizedDirection = new Vector3().copy(direction).normalize();

    const maxDimension = Math.max(
      boundingBox.max.x - boundingBox.min.x,
      boundingBox.max.y - boundingBox.min.y,
      boundingBox.max.z - boundingBox.min.z
    );
    const arrowLength = maxDimension * 0.5;

    if (normalizedDirection.x !== 0) {
      startPosition.clone()[normalizedDirection.x > 0 ? 'sub' : 'add'](new Vector3(arrowLength / 2, 0, 0));
    }
    if (normalizedDirection.y !== 0) {
      startPosition.clone()[normalizedDirection.y > 0 ? 'sub' : 'add'](new Vector3(0, arrowLength / 2, 0));
    }
    if (normalizedDirection.z !== 0) {
      startPosition.clone()[normalizedDirection.z > 0 ? 'sub' : 'add'](new Vector3(0, 0, arrowLength / 2));
    }

    const arrowGroup = new Group();

    // ArrowHelper draws half its length from its origin, so it gets twice the drawn length.
    const head = arrowHead(arrowLength, overlayModelSize(boundingBox.getSize(new Vector3())));
    const arrow = new ArrowHelper(
      'line',
      normalizedDirection.clone(),
      startPosition.clone(),
      arrowLength * 2,
      OVERLAY_COLORS.drawDirection,
      head.length,
      head.radius
    );
    arrow.traverse((child) => child.layers.set(0));
    arrowGroup.add(arrow);

    this.ctx.ucsFrame.add(arrowGroup);
    this.ctx.arrowHelper = arrowGroup;

    const targetPosition = new Vector3().copy(startPosition).addScaledVector(normalizedDirection, distance);
    const animState = { x: startPosition.x, y: startPosition.y, z: startPosition.z };
    const targetState = { x: targetPosition.x, y: targetPosition.y, z: targetPosition.z };

    const tween = new Tween(animState)
      .to(targetState, 1200)
      .easing(Easing.Quadratic.InOut)
      .yoyo(true)
      .repeat(Infinity)
      .onUpdate(() => {
        arrow.position.set(animState.x, animState.y, animState.z);
      });

    this.ctx.arrowHelperTween = tween.start() as unknown as Tween<Record<string, number>>;
  }

  showArrow(): void {
    if (this.ctx.arrowHelper) {
      this.ctx.arrowHelper.visible = true;
    }
  }

  hideArrow(): void {
    if (this.ctx.arrowHelper) {
      this.ctx.arrowHelper.visible = false;
    }
  }

  disposeArrow(): void {
    // The arrow's tween repeats forever on TWEEN's shared default group, which
    // the render loop keeps updating. Left running it keeps writing into the
    // disposed arrow and keeps it reachable.
    this.ctx.arrowHelperTween?.stop();
    this.ctx.arrowHelperTween = undefined;

    if (this.ctx.arrowHelper) {
      disposeOverlay(this.ctx.arrowHelper);
      this.ctx.arrowHelper = undefined;
    }
  }
}
